<template>
  <Modal
    :title="!readOnly ? 'Change Membership Program' : 'Membership Program'"
    @hide-modal="$emit('hide-modal')"
  >
    <div>
      <SelectBox
        v-if="!readOnly"
        v-model="membershipId"
        :options="options"
        placeholder="Select Membership Program"
        :validation-errors="v$.$errors"
        name="Membership Program"
      />
      <h5 v-if="readOnly && selected">
        {{ selected.name }}
      </h5>
      <div v-if="selected" class="mt-3 details">
        {{ selected.description }}
      </div>
      <Alert v-if="!readOnly" type="warning" class="mt-3">
        <span>
          Updating the membership program will reset any price overrides on the
          quote.
        </span>
      </Alert>
    </div>
    <template #submit>
      <div v-if="readOnly" />
      <Button
        v-if="!readOnly"
        :disabled="!membershipId || saving"
        @click="handleSave"
      >
        <span v-if="!saving">Update Membership</span>
        <Spinner v-else message="Saving..." />
      </Button>
    </template>
  </Modal>
</template>

<script>
import { mapActions, mapState } from "vuex";

import Button from "@/components/Button.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Modal from "./Modal.vue";
import SelectBox from "./SelectBox.vue";
import Spinner from "./Spinner.vue";

export default {
  components: {
    Alert,
    Button,
    Modal,
    SelectBox,
    Spinner
  },
  props: {
    quote: {
      type: Object,
      default: () => ({})
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ""
    }
  },
  emits: ["hide-modal"],
  data() {
    return {
      membershipId: null,
      saving: false
    };
  },
  computed: {
    ...mapState({
      activeMemberships({ membershipPrograms }) {
        if (!membershipPrograms) {
          return [];
        }
        return membershipPrograms.filter((mp) => mp.is_active);
      },
      options() {
        return this.activeMemberships.map((mp) => ({
          value: mp.id,
          label: `${mp.name}${mp.set_as_standard ? " (Standard)" : ""}`
        }));
      },
      selected({ membershipPrograms }) {
        return membershipPrograms.find((mp) => mp.id === this.membershipId);
      },
      details() {
        return this.selected ? this.selected.description : "";
      }
    })
  },
  watch: {
    activeMemberships(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.setMembershipId();
      }
    }
  },
  mounted() {
    if (!this.membershipPrograms) {
      this.fetchMembershipPrograms();
    }
  },
  setup() {
    return {
      v$: useVuelidate()
    };
  },
  validations() {
    return {
      membershipId: { required, $autoDirty: true }
    };
  },
  methods: {
    ...mapActions(["fetchMembershipPrograms", "updateQuoteMembershipProgram"]),
    handleSave() {
      this.v$.$validate().then((valid) => {
        if (valid) {
          this.saving = true;
          let payload = {};
          if (this.type === "actual") {
            payload = {
              actual_membership_program_id: this.membershipId,
              comparison_membership_program_id:
                this.quote.comparison_membership_program_id
            };
          } else {
            payload = {
              actual_membership_program_id:
                this.quote.actual_membership_program_id,
              comparison_membership_program_id: this.membershipId
            };
          }
          this.updateQuoteMembershipProgram({
            quote: this.quote,
            data: payload
          })
            .then(() => {
              this.$emit("hide-modal");
              this.saving = false;
            })
            .catch(() => (this.saving = false));
        }
      });
    },
    setMembershipId() {
      if (this.quote) {
        const id =
          this.type === "actual"
            ? this.quote.actual_membership_program_id
            : this.quote.comparison_membership_program_id;

        if (this.activeMemberships.map((mp) => mp.id).includes(id)) {
          this.membershipId = id;
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.details {
  white-space: pre-line;
  border-radius: 5px;
  padding: 15px;
  background-color: var(--white);
  box-shadow: var(--box-shadow);
  color: var(--gray);
}
h3 {
  color: var(--darkblue);
}
</style>
